import React, { useState } from 'react'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Box from '@mui/material/Box'
import CollapsibleTable from './CollapsibleTable'
import { useAdminPermission } from 'src/hooks/IAM/AdminPermission/useAdminPermission'
import { INPermission } from 'src/services/IAM/AdminPermissions/AdminPermissions.interface'

function CustomTabPanel(props: {
  children?: React.ReactNode
  index: number
  value: number
}) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, minWidth: '780px' }}>{children}</Box>
      )}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  }
}

/**
 * StepperModal Component
 *
 * The StepperModal component provides a user interface for managing permissions
 * in a tabbed layout. It allows users to view all available permissions and 
 * the permissions they have selected through two distinct tabs: "Todos los 
 * permisos" and "Seleccionados". The permissions are organized in a 
 * collapsible table format, improving accessibility and usability.
 *
 * Features:
 * - Tabbed navigation to toggle between all permissions and selected permissions.
 * - Integration with the `CollapsibleTable` component to display permissions 
 *   in a user-friendly manner.
 * - Supports loading permissions dynamically based on pagination.
 * 
 * @param {Props} props
 * Props:
 * - `permissionsProp`: An array of permissions available for selection, of 
 *   type `INPermission[]`.
 * - `selectedPermissions`: An array of permissions that the user has selected,
 *   of type `INPermission[]`.
 * - `loadPermissions`: A function that retrieves permissions based on current 
 *   pagination parameters, accepting an object with `currentPage` and 
 *   `pageSize`.
 * - `handlerSelectedPermissions`: A callback function that handles the 
 *   selection of permissions, accepting a single permission of type 
 *   `INPermission`.
 *
 * State:
 * - `value`: The index of the currently active tab, initialized to 0 (first 
 *   tab).
 *
 * Usage:
 * This component is typically used in applications that require user 
 * permissions management, offering a clear distinction between available 
 * and selected permissions.
 * 
 *
 * @component
 * @example
 * return (
 *   <StepperModal 
 *     permissionsProp={permissions} 
 *     selectedPermissions={selected} 
 *     loadPermissions={loadPermissionsFunction} 
 *     handlerSelectedPermissions={handleSelectedPermissionsFunction} 
 *   />
 * )
 * @returns {JSX.Element} El componente JSX que renderiza la interfaz de administración de usuarios.
 */

export default function StepperModal({
  permissionsProp,
  loadPermissions,
  selectedPermissions,
  handlerSelectedPermissions,
}: {
  permissionsProp: INPermission[]
  selectedPermissions: INPermission[]
  loadPermissions: (params: { currentPage: number; pageSize: number }) => void
  handlerSelectedPermissions: (permissions: INPermission) => void
}) {
  const [value, setValue] = React.useState(0)
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab label="Todos los permisos" {...a11yProps(0)} />
          <Tab label="Seleccionados" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <CollapsibleTable
          permissionsProp={permissionsProp}
          loadPermissions={loadPermissions}
          handlerSelectedPermissions={handlerSelectedPermissions}
        />
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <CollapsibleTable
          selectedPermissions={selectedPermissions}
          loadPermissions={loadPermissions}
          handlerSelectedPermissions={handlerSelectedPermissions}
        />
      </CustomTabPanel>
    </Box>
  )
}
